'use client';

import React, { useState } from 'react';
import Image from 'next/image';

interface AppImageProps {
    src: string;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
    priority?: boolean;
    quality?: number;
    placeholder?: 'blur' | 'empty';
    blurDataURL?: string;
    fill?: boolean;
    sizes?: string;
    onClick?: () => void;
    fallbackSrc?: string;
    [key: string]: any;
}

function AppImage({
    src,
    alt,
    width,
    height,
    className = '',
    priority = false,
    quality = 85,
    placeholder = 'empty',
    blurDataURL,
    fill = false,
    sizes,
    onClick,
    fallbackSrc = '/assets/images/no_image.png',
    ...props
}: AppImageProps) {
    const [imageSrc, setImageSrc] = useState(src);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    // More reliable external URL detection
    const isExternal = imageSrc.startsWith('http://') || imageSrc.startsWith('https://');
    const isLocal = imageSrc.startsWith('/') || imageSrc.startsWith('./') || imageSrc.startsWith('data:');

    const handleError = () => {
        if (!hasError && imageSrc !== fallbackSrc) {
            setImageSrc(fallbackSrc);
            setHasError(true);
        }
        setIsLoading(false);
    };

    const handleLoad = () => {
        setIsLoading(false);
        setHasError(false);
    };

    const commonClassName = `${className} ${isLoading ? 'animate-pulse bg-gray-200' : ''} ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`;

    // Use Next.js Image component for ALL images to enable WebP conversion
    if (fill) {
        return (
            <div className={`relative ${className}`} style={{ width: width || '100%', height: height || '100%' }} onClick={onClick}>
                <Image
                    src={imageSrc}
                    alt={alt}
                    fill
                    className={commonClassName}
                    quality={quality}
                    priority={priority}
                    sizes={sizes || '100vw'}
                    onError={handleError}
                    onLoad={handleLoad}
                    unoptimized={false}
                    {...props}
                />
            </div>
        );
    }

    // For images with specific dimensions
    if (width && height) {
        return (
            <Image
                src={imageSrc}
                alt={alt}
                width={width}
                height={height}
                className={commonClassName}
                quality={quality}
                priority={priority}
                sizes={sizes}
                onError={handleError}
                onLoad={handleLoad}
                onClick={onClick}
                unoptimized={false}
                {...props}
            />
        );
    }

    // Fallback: use img tag with explicit dimensions if Next.js Image requirements not met
    const imgStyle: React.CSSProperties = {};
    if (width) imgStyle.width = width;
    if (height) imgStyle.height = height;

    return (
        <img
            src={imageSrc}
            alt={alt}
            className={commonClassName}
            onError={handleError}
            onLoad={handleLoad}
            onClick={onClick}
            style={imgStyle}
            {...props}
        />
    );
}

export default AppImage;